import { isWidgetMessage, safeHostPageUrl } from "./protocol";

const FRAME_ID_PREFIX = "custom-rag-chatbot-frame";

export function findLoaderScript(
  documentRoot: Document,
  moduleUrl: string,
): HTMLScriptElement | null {
  const expectedUrl = new URL(moduleUrl, documentRoot.baseURI).href;
  for (const candidate of documentRoot.querySelectorAll<HTMLScriptElement>("script[data-bot-id]")) {
    if (candidate.src && new URL(candidate.src, documentRoot.baseURI).href === expectedUrl) {
      return candidate;
    }
  }
  return null;
}

function resizeFrame(frame: HTMLIFrameElement, state: "compact" | "open"): void {
  frame.dataset.state = state;
  if (state === "compact") {
    frame.style.width = "64px";
    frame.style.height = "64px";
    frame.style.borderRadius = "999px";
    frame.style.boxShadow = "none";
    return;
  }
  frame.style.width = "min(420px, calc(100vw - 24px))";
  frame.style.height = "min(720px, calc(100dvh - 24px))";
  frame.style.borderRadius = "24px";
  frame.style.boxShadow = "0 24px 80px rgba(15,23,42,.24)";
}

export function mountWidget(script: HTMLScriptElement, documentRoot: Document = document): void {
  const botId = script.dataset.botId?.trim();
  if (!botId || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(botId)) {
    throw new Error("Custom RAG Chatbot loader requires data-bot-id");
  }
  const frameId = `${FRAME_ID_PREFIX}-${botId}`;
  if (documentRoot.getElementById(frameId)) return;

  const baseUrl = script.dataset.widgetUrl
    ? new URL(script.dataset.widgetUrl, documentRoot.baseURI)
    : new URL("./", script.src);
  const frameUrl = new URL(`${encodeURIComponent(botId)}/index.html`, baseUrl);
  frameUrl.searchParams.set("bot", botId);
  const frameOrigin = frameUrl.origin;

  const frame = documentRoot.createElement("iframe");
  frame.id = frameId;
  frame.title = script.dataset.title?.trim() || "AI assistant";
  frame.src = frameUrl.toString();
  frame.loading = "lazy";
  frame.referrerPolicy = "strict-origin-when-cross-origin";
  frame.setAttribute(
    "sandbox",
    "allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts",
  );
  frame.style.cssText = [
    "position:fixed",
    "right:16px",
    "bottom:16px",
    "width:64px",
    "height:64px",
    "border:0",
    "border-radius:999px",
    "box-shadow:none",
    "z-index:2147483000",
    "background:transparent",
    "color-scheme:light",
  ].join(";");

  const parentWindow = documentRoot.defaultView;
  parentWindow?.addEventListener("message", (event) => {
    if (
      event.source !== frame.contentWindow ||
      event.origin !== frameOrigin ||
      !isWidgetMessage(event.data, botId)
    ) {
      return;
    }
    if (event.data.type === "rag-widget-ready") {
      frame.contentWindow?.postMessage(
        {
          type: "rag-host-context",
          version: 1,
          botId,
          pageUrl: safeHostPageUrl(parentWindow.location),
        },
        frameOrigin,
      );
      return;
    }
    resizeFrame(frame, event.data.state);
  });

  documentRoot.body.append(frame);
}
